import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "../../componentes/PageHeader";
import Sidebar from "../../componentes/Sidebar";
import GradientBanner from "../../componentes/Banner";
import { Link, useNavigate } from "react-router-dom";
import LoadingSkeleton from "../../componentes/LoadingSkeleton";
import { useTranslation } from "react-i18next";
import MetaTags from "../../componentes/MetaTags";
import { fetchAdvisories } from "../../api/advisories";
import { formatDate } from "../../utils/dateFormatter";
import {
  faExclamationTriangle,
  faShieldAlt,
  faVirusSlash,
  faRadiation,
  faBug,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";
import { API_BASE_URL } from '../../api/config';

const defaultImage = "https://c.animaapp.com/7PaZ1XR8/img/download-2-2@2x.png";

const baseUrl = API_BASE_URL;

// Strip HTML tags for card description previews
const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

// API fetch functions for news and events
const fetchNews = async (lang) => { // Add lang parameter
  const response = await fetch(`${baseUrl}/api/news?lang=${lang}`); // Include lang in query
  return response.json();
};

const fetchEvents = async (lang) => { // Add lang parameter
  const response = await fetch(`${baseUrl}/api/events?lang=${lang}`); // Include lang in query
  return response.json();
};

const Advisories = () => {
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // Fetch advisories (main content)
  const { data, isLoading, error } = useQuery({
    queryKey: ["advisories", { dateRange, page, lang }],
    queryFn: () => fetchAdvisories({ dateRange, page, lang }),
  });

  // Fetch news and events for sidebar using useQuery
  const {
      data: newsData,
      isLoading: newsLoading,
    } = useQuery({
      queryKey: ["news", lang], // Add lang to queryKey
      queryFn: () => fetchNews(lang), // Pass lang to fetchNews
    });
  
    const {
      data: eventsData,
      isLoading: eventsLoading,
    } = useQuery({
      queryKey: ["events", lang], // Add lang to queryKey
      queryFn: () => fetchEvents(lang), // Pass lang to fetchEvents
    });
  

  const advisories = data?.results || [];
  const totalPages = Math.ceil((data?.count || 0) / 5);
  const news = newsData?.results || newsData || [];
  const events = eventsData?.results || eventsData || [];
  const loadingSidebar = newsLoading || eventsLoading;

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const truncateText = (text, maxLength = 150) => {
    const strippedText = stripHtml(text);
    return strippedText.length <= maxLength
      ? strippedText
      : strippedText.substring(0, maxLength) + "...";
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  if (isLoading) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("advisories.title")}
          description={t("advisories.subtitle")}
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("advisories.title"), link: null },
          ]}
          backgroundIcons={[
            faExclamationTriangle,
            faShieldAlt,
            faVirusSlash,
            faBug,
            faRadiation,
          ]}
        />
        <FullscreenSpinner />
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("advisories.title")}
          description={t("advisories.subtitle")}
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("advisories.title"), link: null },
          ]}
          backgroundIcons={[
            faExclamationTriangle,
            faShieldAlt,
            faVirusSlash,
            faBug,
            faRadiation,
          ]}
        />
        <div className="flex justify-center items-center min-h-[400px]">
          <p className="text-red-500 font-nunito-sans text-lg">
            {t("advisories.error", { message: error.message })}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <MetaTags title="Advisories — Ethio-CERT" description="Security advisories and vulnerability disclosures from Ethio-CERT." url="https://ethiocert.gov.et/advisories" />
      <PageHeader />
      <GradientBanner
        title={t("advisories.title")}
        description={t("advisories.subtitle")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("advisories.title"), link: null },
        ]}
        backgroundIcons={[
          faExclamationTriangle,
          faShieldAlt,
          faVirusSlash,
          faBug,
          faRadiation,
        ]}
      />
      <div className="min-h-screen">
        <div className="flex flex-col max-xl:px-4 py-4 md:py-8 px-dynamic">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-2 xl:gap-6 mt-6">
            <div className="md:col-span-2">
              <div className="flex flex-col gap-6 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex flex-col w-full sm:w-auto">
                    <label
                      htmlFor="start-date"
                      className="absolute -top-2.5 left-2 bg-white px-1 text-xs font-medium text-gray-600 uppercase tracking-wider"
                    >
                      {t("advisories.filters.start_date")}
                    </label>
                    <input
                      id="start-date"
                      type="date"
                      name="start"
                      onChange={handleFilterChange}
                      value={dateRange.start}
                      max={dateRange.end || undefined}
                      className="w-full border-2 border-primary/20 rounded-md pt-3 pb-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-nunito-sans text-sm"
                    />
                  </div>
                  <div className="relative flex flex-col w-full sm:w-auto">
                    <label
                      htmlFor="end-date"
                      className="absolute -top-2.5 left-2 bg-white px-1 text-xs font-medium text-gray-600 uppercase tracking-wider"
                    >
                      {t("advisories.filters.end_date")}
                    </label>
                    <input
                      id="end-date"
                      type="date"
                      name="end"
                      onChange={handleFilterChange}
                      value={dateRange.end}
                      min={dateRange.start || undefined}
                      className="w-full border-2 border-primary/20 rounded-md pt-3 pb-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-nunito-sans text-sm"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                {advisories.length > 0 ? (
                  advisories.map((advisory) => {
                    const hasImage = advisory.image;
                    
                    return (
                      <Link
                        key={advisory.id}
                        to={`/advisories/${advisory.slug}`}
                        className="block group hover:no-underline"
                      >
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className="bg-white rounded-xl border border-gray-200 hover:border-primary/30 hover:shadow-lg transition-all duration-300 overflow-hidden"
                        >
                        <div className={`flex ${hasImage ? 'max-sm:flex-col' : 'flex-col'}`}>
                          {hasImage && (
                            <div className="w-2/5 aspect-[16/10] max-sm:w-full flex-shrink-0 relative overflow-hidden">
                              <img
                                src={advisory.image}
                                alt={advisory.title || "Advisory"}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                onError={(e) => {
                                  e.target.src = defaultImage;
                                }}
                              />
                            </div>
                          )}
                          
                          <div className={`flex-1 min-w-0 p-6 ${!hasImage ? 'border-l-4 border-primary/20' : ''}`}>
                            <div className="flex items-center gap-3 mb-3">
                              <span className="flex items-center text-sm text-gray-500 font-nunito-sans">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 mr-2 text-primary" />
                                {formatDate(advisory.created_at, lang)}
                              </span>
                            </div>
                            
                            <h2 className="font-catamaran text-xl font-bold text-gray-900 mb-3 line-clamp-2 break-words group-hover:text-primary transition-colors duration-300">
                              {advisory.title || "No Title"}
                            </h2>
                            
                            <p className="font-nunito-sans text-gray-600 text-sm line-clamp-3 break-words leading-relaxed">
                              {truncateText(advisory.description || "No Description")}
                            </p>
                            
                            <div className="mt-4 flex items-center justify-between">
                              <span className="text-primary font-nunito-sans text-sm font-medium group-hover:underline">
                                {t("advisories.read_more")}
                              </span>
                              {!hasImage && (
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-gray-300 text-2xl" />
                              )}
                            </div>
                          </div>
                        </div>
                        </motion.div>
                      </Link>
                    );
                  })
                ) : (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-16"
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-gray-300 text-6xl mb-4" />
                    <h3 className="font-catamaran text-xl font-bold text-gray-700 mb-2">
                      No Advisories Found
                    </h3>
                    <p className="font-nunito-sans text-gray-500">
                      {t("advisories.no_results")}
                    </p>
                  </motion.div>
                )}
              </div>
              {totalPages > 1 && (
                <div className="mt-8 p-2 flex justify-center items-center gap-2">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className={`px-4 py-2 rounded-md font-nunito-sans text-sm transition-colors duration-200 ${
                      page === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-primary text-white hover:bg-primary/90"
                    }`}
                  >
                    {t("common.previous")}
                  </button>
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => handlePageChange(index + 1)}
                      className={`px-4 py-2 rounded-md font-nunito-sans text-sm transition-colors duration-200 ${
                        page === index + 1
                          ? "bg-primary text-white"
                          : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className={`px-4 py-2 rounded-md font-nunito-sans text-sm transition-colors duration-200 ${
                      page === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-primary text-white hover:bg-primary/90"
                    }`}
                  >
                    {t("common.next")}
                  </button>
                </div>
              )}
            </div>
            <Sidebar
              primaryItems={news}
              secondaryItems={events}
              loading={loadingSidebar}
              primaryTitle="news"
              secondaryTitle="events"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Advisories;